import {QueryResult, registerBrick} from 'olympe';
import {ReactBrick} from "@olympeio/core";
import React, {useEffect, useRef} from "react";
import {combineLatest} from "rxjs";
import * as echarts from 'echarts';
import {Box} from "@mui/material";
import {format} from "date-fns";


export default class CandleStickChart extends ReactBrick {

    /**
     * @override
     */
    setupExecution($) {
        return combineLatest([
            $.observe('Hidden', false),
            $.observe('Width', false),
            $.observe('Height', false),
            $.observe('candles', false),
            $.observe('data', false),
            $.observe('markers', false),
            $.observe('markLines', false),
            $.observe('secondChartData', false),
        ]);
    }
    /**
     * @override
     */
    static getReactComponent($) {

        return (props) => {
            const [hidden, width, height, rawCandles, otherData, markers, markLines, secondChart] = props.values;
            const chartRef = useRef(null);
            const chartInstance = useRef(null); // Store ECharts instance


            useEffect(() => {
                if (!chartRef.current) return;

                // Dispose of the previous chart instance before initializing a new one
                if (chartInstance.current) {
                    chartInstance.current.dispose();
                }

                chartInstance.current = echarts.init(chartRef.current);

                let jsonCandles = rawCandles ?? [];
                if (rawCandles instanceof QueryResult) {
                    jsonCandles = rawCandles.toArray().map(c => c.toJSON());
                }

                const dates = jsonCandles.map((item) => {
                    if (Array.isArray(item)) {
                        return item[0];
                    } else {
                        return format(new Date(item.openTime ?? item.timeFrom), 'dd.MM.yyyy HH:mm');
                    }
                });
                const data = jsonCandles.map((item) => {
                    if (Array.isArray(item)) {
                        return [+item[1], +item[2], +item[3], +item[4]];
                    } else {
                        return [item.open, item.close, item.low, item.high];
                    }
                });

                const otherLines = otherData?.map(d => {
                    const lineDescription = {
                        name: d.name,
                        type: 'line',
                        data: d.data,
                        smooth: true,
                        showSymbol: false,
                        lineStyle: {
                            width: 1
                        }
                    }

                    if (d.color) {
                        lineDescription.color = d.color
                    }

                    return lineDescription;
                }) ?? [];

                const markersList = markers?.map(d => {
                    const markerDescription = {
                        name: d.name,
                        coord: d.coord,
                        value: d.value
                    }

                    if (d.color) {
                        markerDescription.itemStyle = { color: d.color }
                    }

                    return markerDescription;
                }) ?? [];

                chartInstance.current.setOption({
                    legend: {
                        data: ['Day', ...(otherData?.map(d => d.name) ?? [])],
                        inactiveColor: '#777'
                    },
                    tooltip: {
                        trigger: 'axis',
                        axisPointer: {
                            animation: false,
                            type: 'cross',
                            lineStyle: {
                                color: '#376df4',
                                width: 2,
                                opacity: 1
                            }
                        }
                    },
                    xAxis: [
                        {
                            type: 'category',
                            data: dates,
                            axisLine: {lineStyle: {color: '#8392A5'}}
                        },
                        {
                            type: 'category',
                            gridIndex: 1,
                            boundaryGap: false,
                            axisLine: { onZero: false },
                            axisTick: { show: false },
                            splitLine: { show: false },
                            axisLabel: { show: false },
                            min: 'dataMin',
                            max: 'dataMax'
                        }
                    ],
                    yAxis: [
                        {
                            scale: true,
                            axisLine: {lineStyle: {color: '#8392A5'}},
                            splitLine: {show: false}
                        },
                        {
                            scale: true,
                            gridIndex: 1,
                            splitNumber: 2,
                            axisLabel: { show: false },
                            axisLine: { show: false },
                            axisTick: { show: false },
                            splitLine: { show: false }
                        }
                    ],
                    grid: [
                        {
                            bottom: 200
                        },
                        {
                            height: 80,
                            bottom: 80
                        }
                    ],
                    dataZoom: [
                        {
                            textStyle: {
                                color: '#8392A5'
                            },
                            handleIcon:
                                'path://M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
                            dataBackground: {
                                areaStyle: {
                                    color: '#8392A5'
                                },
                                lineStyle: {
                                    opacity: 0.8,
                                    color: '#8392A5'
                                }
                            },
                            xAxisIndex: [0, 1],
                            brushSelect: true
                        },
                        {
                            type: 'inside',
                            xAxisIndex: [0, 1],
                        }
                    ],
                    series: [
                        {
                            type: 'candlestick',
                            name: 'Day',
                            data: data,
                            itemStyle: {
                                color: '#0CF49B',
                                color0: '#FD1050',
                                borderColor: '#0CF49B',
                                borderColor0: '#FD1050'
                            },
                            markPoint: {
                                label: {
                                    formatter: function (param) {
                                        let myValue = '';
                                        if (param && typeof param.value === 'number') {
                                            myValue = Number(param.value.toPrecision(3)).toString();
                                        } else if (param) {
                                            myValue = param;
                                        }
                                        return myValue;
                                    }
                                },
                                data: markersList
                            },
                            markLine: {
                                symbol: ['none', 'none'],
                                data: markLines
                            }
                        },
                        {
                            name: 'Balance',
                            data: secondChart ?? [],
                            type: 'bar',
                            xAxisIndex: 1,
                            yAxisIndex: 1,
                            itemStyle: {
                                color: '#7fbe9e'
                            },
                            large: true,
                            encode: {
                                x: 0,
                                y: 5
                            }
                        },
                        ...otherLines,
                    ]
                });

                return () => {
                    // Dispose the chart when the component unmounts
                    if (chartInstance.current) {
                        chartInstance.current.dispose();
                        chartInstance.current = null;
                    }
                };
            }, [hidden, width, height, rawCandles, otherData, markers, markLines, secondChart]); // Dependencies to update chart

            if (!hidden) {
                return (<Box ref={chartRef} sx={{width: 1, height: 1}}></Box>);
            } else {
                return (<Box sx={{width: 1, height: 1}}></Box>);
            }
        }
    }
}

registerBrick('0194df5e71b19523cce2', CandleStickChart);
